/**
 * Downloads an image from a URL and converts it to a File object
 * @param {string} url - The URL of the image to download
 * @param {string} filename - Optional filename for the downloaded image
 * @param {string} apiBaseUrl - Base URL of the API for proxy requests
 * @returns {Promise<{file: File, previewUrl: string}>} The File object and preview URL
 * 
 * This function first tries direct fetch with CORS, and if that fails due to CORS restrictions,
 * it falls back to using a backend proxy endpoint to download the image server-side.
 */
export async function downloadImageAsFile(url, filename, apiBaseUrl) {
	try {
		const proxyUrl = `${apiBaseUrl}/Recipes/proxy-image?url=${encodeURIComponent(url)}`;
		const response = await fetch(proxyUrl, {
			credentials: 'include'
		});

		if (!response.ok) {
			throw new Error(`Proxy fetch failed: ${response.status} ${response.statusText}`);
		}

		const blob = await response.blob();
		return createFileFromBlob(blob, url, filename);
	} catch (error) {
		console.error('Error downloading image:', error);
		throw error;
	}
}

/**
 * Helper function to create File and preview URL from blob
 */
function createFileFromBlob(blob, originalUrl, filename) {
	// Extract filename from URL if not provided
	if (!filename) {
		const urlPath = new URL(originalUrl).pathname;
		const urlFilename = urlPath.substring(urlPath.lastIndexOf('/') + 1);
		filename = urlFilename || 'imported-image';

		// If no extension, try to get from content-type
		if (!filename.includes('.')) {
			const extension = blob.type.split('/')[1]?.split(';')[0];
			if (extension) {
				filename = `${filename}.${extension}`;
			}
		}
	}

	// Create a File object from the blob
	const file = new File([blob], filename, { type: blob.type });

	// Create preview URL
	const previewUrl = URL.createObjectURL(blob);

	return { file, previewUrl };
}

/**
 * Downloads multiple images from URLs
 * @param {string[]} urls - Array of image URLs
 * @param {string} apiBaseUrl - Base URL of the API for proxy requests
 * @returns {Promise<Array<{file: File, previewUrl: string}>>} Array of File objects and preview URLs
 */
export async function downloadImagesAsFiles(urls, apiBaseUrl) {
	if (!urls || !Array.isArray(urls) || urls.length === 0) {
		return [];
	}

	// Filter out null/undefined/empty URLs
	const validUrls = urls.filter(url => url && typeof url === 'string' && url.trim().length > 0);

	// Download all images in parallel, preserving original filenames
	const results = await Promise.allSettled(
		validUrls.map((url) => downloadImageAsFile(url, null, apiBaseUrl))
	);

	// Return only successful downloads
	return results
		.filter(result => result.status === 'fulfilled')
		.map(result => result.value);
}
